import React, { useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Box,
  BlockStack,
  InlineStack,
  Badge,
  Icon,
  Divider,
  Grid,
  InlineGrid,
  CalloutCard,
  MediaCard,
  VideoThumbnail,
  List,
  ProgressBar
} from '@shopify/polaris';
import {
  EmailMajor,
  OrdersMajor,
  SettingsMajor,
  ChecklistMajor,
  HomeMajor,
  CircleTickMajor,
  LanguageMinor,
  AutomationMajor,
  PackageMajor
} from '@shopify/polaris-icons';
import { useTranslation } from 'react-i18next';

const HowItWorks = () => {
  const { t } = useTranslation();
  const [selectedEmailIndex, setSelectedEmailIndex] = useState(null);

  // Email templates from the txt file
  const emailTemplates = [
    "We're preparing your order for shipment.",
    "Your product has been picked and is waiting for logistics processing.",
    "Your package has been successfully packed and is waiting to be collected.",
    "Your order is being processed by our logistics team.",
    "Packing completed! Your package will soon begin its journey.",
    "Your order has been picked up by the local carrier.",
    "The warehouse has confirmed collection of your package.",
    "Update: Item received at the global logistics center.",
    "Your parcel is being prepared for transfer between logistics hubs.",
    "Your package has left the distribution center.",
    "Order dispatched from the main warehouse.",
    "The parcel has been sent to the next logistics facility.",
    "Your package is waiting for pick-up by the local carrier.",
    "Your order is currently at the local distribution center.",
    "The package was scanned at the regional delivery center.",
    "Your order will soon be routed to the final distribution center.",
    "Delivery scheduling is underway with your regional carrier.",
    "Routine update: We are continuing to monitor your delivery.",
    "We're tracking your package closely—no issues detected so far.",
    "All is well! Your order remains in transit.",
    "Your order is out for delivery! Our carrier is on the way to your address. Please make sure someone is available to receive your package.",
    "Your order has been delivered! We hope you enjoy your purchase. If you have any questions or feedback, please let us know."
  ];

  // Available languages
  const availableLanguages = [
    'Bulgarian', 'Czech', 'Danish', 'German', 'Greek', 'English',
    'Spanish', 'Estonian', 'French', 'Croatian', 'Hungarian', 'Italian',
    'Dutch', 'Norwegian', 'Polish', 'Portuguese', 'Romanian', 'Russian',
    'Slovak', 'Slovenian', 'Serbian', 'Swedish'
  ];

  const getEmailTiming = (index) => {
    if (index === 0) return t('howItWorks.emailTiming.after2Hours');
    if (index >= 1 && index <= 5) return t('howItWorks.emailTiming.every6Hours');
    if (index >= 6 && index <= 19) return t('howItWorks.emailTiming.every24Hours');
    if (index === 20) return t('howItWorks.emailTiming.outForDelivery');
    if (index === 21) return t('howItWorks.emailTiming.delivered');
  };

  const getEmailCategory = (index) => {
    if (index <= 5) return { label: t('howItWorks.emailCategories.preparation'), color: 'info' };
    if (index <= 19) return { label: t('howItWorks.emailCategories.transit'), color: 'warning' };
    if (index === 20) return { label: t('howItWorks.emailCategories.outForDelivery'), color: 'attention' };
    if (index === 21) return { label: t('howItWorks.emailCategories.delivered'), color: 'success' };
  };

  return (
    <Page
      title={t('howItWorks.title')}
      breadcrumbs={[{ content: t('navigation.dashboard'), url: '/' }]}
    >
      <Layout>
        {/* Hero Section - Modern Design */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <BlockStack gap="200">
                  <Text as="h2" variant="headingXl">
                    {t('howItWorks.heroTitle')}
                  </Text>
                  <Text as="p" variant="bodyLg" color="subdued">
                    {t('howItWorks.heroDescription')}
                  </Text>
                </BlockStack>
                
                {/* Key Statistics */}
                <InlineGrid columns={{ xs: '2', sm: '4' }} gap="400">
                  <Card padding="300">
                    <BlockStack gap="200" align="center">
                      <Text as="h3" variant="headingMd" fontWeight="bold" tone="success">
                        80-90%
                      </Text>
                      <Text variant="bodySm" tone="subdued" alignment="center">
                        {t('howItWorks.stats.supportReduction')}
                      </Text>
                    </BlockStack>
                  </Card>
                  
                  <Card padding="300">
                    <BlockStack gap="200" align="center">
                      <Text as="h3" variant="headingMd" fontWeight="bold" tone="success">
                        80%+
                      </Text>
                      <Text variant="bodySm" tone="subdued" alignment="center">
                        {t('howItWorks.stats.chargebackReduction')}
                      </Text>
                    </BlockStack>
                  </Card>
                  
                  <Card padding="300">
                    <BlockStack gap="200" align="center">
                      <Text as="h3" variant="headingMd" fontWeight="bold" tone="success">
                        22
                      </Text>
                      <Text variant="bodySm" tone="subdued" alignment="center">
                        {t('howItWorks.stats.languages')}
                      </Text>
                    </BlockStack>
                  </Card>
                  
                  <Card padding="300">
                    <BlockStack gap="200" align="center">
                      <Text as="h3" variant="headingMd" fontWeight="bold" tone="success">
                        24/7
                      </Text>
                      <Text variant="bodySm" tone="subdued" alignment="center">
                        {t('howItWorks.stats.aiSupport')}
                      </Text>
                    </BlockStack>
                  </Card>
                </InlineGrid>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* How It Works Overview - Modern Design */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <Text variant="headingMd" as="h2">
                {t('howItWorks.systemOverview.title')}
              </Text>
            </Box>
            <Divider />
            
            <Box padding="400">
              <InlineGrid columns={{ xs: '1', sm: '2', md: '3' }} gap="400">
                <Card padding="400">
                  <InlineStack gap="300" align="start">
                    <div style={{ 
                      backgroundColor: '#e3f1df', 
                      padding: '12px', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon source={EmailMajor} color="success" />
                    </div>
                    <BlockStack gap="200">
                      <Text variant="headingSm" as="h3">{t('howItWorks.features.automation.title')}</Text>
                      <Text color="subdued">{t('howItWorks.features.automation.description')}</Text>
                    </BlockStack>
                  </InlineStack>
                </Card>

                <Card padding="400">
                  <InlineStack gap="300" align="start">
                    <div style={{ 
                      backgroundColor: '#e0f5ff', 
                      padding: '12px', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon source={OrdersMajor} color="highlight" />
                    </div>
                    <BlockStack gap="200">
                      <Text variant="headingSm" as="h3">{t('howItWorks.features.tracking.title')}</Text>
                      <Text color="subdued">{t('howItWorks.features.tracking.description')}</Text>
                    </BlockStack>
                  </InlineStack>
                </Card>

                <Card padding="400">
                  <InlineStack gap="300" align="start">
                    <div style={{ 
                      backgroundColor: '#fff7ed', 
                      padding: '12px', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon source={LanguageMinor} color="warning" />
                    </div>
                    <BlockStack gap="200">
                      <Text variant="headingSm" as="h3">{t('howItWorks.features.multilingual.title')}</Text>
                      <Text color="subdued">{t('howItWorks.features.multilingual.description')}</Text>
                    </BlockStack>
                  </InlineStack>
                </Card>

                <Card padding="400">
                  <InlineStack gap="300" align="start">
                    <div style={{ 
                      backgroundColor: '#f0f9ff', 
                      padding: '12px', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon source={ChecklistMajor} color="primary" />
                    </div>
                    <BlockStack gap="200">
                      <Text variant="headingSm" as="h3">{t('howItWorks.features.aiSupport.title')}</Text>
                      <Text color="subdued">{t('howItWorks.features.aiSupport.description')}</Text>
                    </BlockStack>
                  </InlineStack>
                </Card>

                <Card padding="400">
                  <InlineStack gap="300" align="start">
                    <div style={{ 
                      backgroundColor: '#f3e8ff', 
                      padding: '12px', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon source={HomeMajor} color="info" />
                    </div>
                    <BlockStack gap="200">
                      <Text variant="headingSm" as="h3">{t('howItWorks.features.integration.title')}</Text>
                      <Text color="subdued">{t('howItWorks.features.integration.description')}</Text>
                    </BlockStack>
                  </InlineStack>
                </Card>

                <Card padding="400">
                  <InlineStack gap="300" align="start">
                    <div style={{ 
                      backgroundColor: '#fef3c7', 
                      padding: '12px', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon source={AutomationMajor} color="accent" />
                    </div>
                    <BlockStack gap="200">
                      <Text variant="headingSm" as="h3">{t('howItWorks.features.apis.title')}</Text>
                      <Text color="subdued">{t('howItWorks.features.apis.description')}</Text>
                    </BlockStack>
                  </InlineStack>
                </Card>
              </InlineGrid>
            </Box>
          </Card>
        </Layout.Section>

        {/* Email Journey Timeline - Modern Design */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <InlineStack gap="300" align="start">
                <div style={{ 
                  backgroundColor: '#e0f5ff', 
                  padding: '12px', 
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icon source={EmailMajor} color="highlight" />
                </div>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h2">
                    {t('howItWorks.emailJourney.title')}
                  </Text>
                  <Text color="subdued">
                    {t('howItWorks.emailJourney.description')}
                  </Text>
                </BlockStack>
              </InlineStack>
            </Box>
            <Divider />
            
            <Box padding="400">
              <BlockStack gap="400">
                <div style={{
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #3b82f6',
                  borderRadius: '6px',
                  padding: '16px'
                }}>
                  <p style={{ color: '#1e40af', margin: '0', lineHeight: '1.5' }}>
                    {t('howItWorks.emailJourney.trackingCodeInfo')}
                  </p>
                </div>

                {/* Email Journey Phases - Modern Visual Timeline */}
                <BlockStack gap="500">
                  {/* Phase 1: Preparation (Emails 1-6) */}
                  <Card padding="400">
                    <BlockStack gap="400">
                      <InlineStack gap="300" align="start">
                        <div style={{ 
                          backgroundColor: '#e0f2fe', 
                          padding: '16px', 
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '60px',
                          minHeight: '60px'
                        }}>
                          <Icon source={PackageMajor} color="info" />
                        </div>
                        <BlockStack gap="200">
                          <InlineStack gap="200" align="start">
                            <Text variant="headingMd" as="h3">📦 {t('howItWorks.emailJourney.phases.preparation.title')}</Text>
                            <Badge tone="info">Emails 1-6</Badge>
                          </InlineStack>
                          <Text color="subdued">
                            {t('howItWorks.emailJourney.phases.preparation.description')}
                          </Text>
                        </BlockStack>
                      </InlineStack>
                      
                      <InlineGrid columns={{ xs: '1', sm: '2', md: '3' }} gap="300">
                        {emailTemplates.slice(0, 6).map((template, index) => (
                          <Card 
                            key={index} 
                            padding="300"
                            onClick={() => setSelectedEmailIndex(selectedEmailIndex === index ? null : index)}
                            style={{ 
                              cursor: 'pointer',
                              border: selectedEmailIndex === index ? '2px solid #0ea5e9' : '1px solid #e5e7eb',
                              backgroundColor: selectedEmailIndex === index ? '#f0f9ff' : 'white'
                            }}
                          >
                            <BlockStack gap="200">
                              <InlineStack gap="200" align="space-between">
                                <Text variant="headingSm" fontWeight="bold">#{index + 1}</Text>
                                <Text variant="bodySm" color="subdued">
                                  {index === 0 ? t('howItWorks.emailJourney.phases.preparation.timing.first') : t('howItWorks.emailJourney.phases.preparation.timing.subsequent')}
                                </Text>
                              </InlineStack>
                              <Text variant="bodyMd" style={{ lineHeight: '1.4' }}>
                                {template}
                              </Text>
                            </BlockStack>
                          </Card>
                        ))}
                      </InlineGrid>
                    </BlockStack>
                  </Card>

                  {/* Phase 2: Transit (Emails 7-20) */}
                  <Card padding="400">
                    <BlockStack gap="400">
                      <InlineStack gap="300" align="start">
                        <div style={{ 
                          backgroundColor: '#fef3c7', 
                          padding: '16px', 
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '60px',
                          minHeight: '60px'
                        }}>
                          <Icon source={EmailMajor} color="warning" />
                        </div>
                        <BlockStack gap="200">
                          <InlineStack gap="200" align="start">
                            <Text variant="headingMd" as="h3">🚚 {t('howItWorks.emailJourney.phases.transit.title')}</Text>
                            <Badge tone="warning">Emails 7-20</Badge>
                          </InlineStack>
                          <Text color="subdued">
                            {t('howItWorks.emailJourney.phases.transit.description')}
                          </Text>
                        </BlockStack>
                      </InlineStack>
                      
                      <InlineGrid columns={{ xs: '1', sm: '2', md: '4' }} gap="300">
                        {emailTemplates.slice(6, 20).map((template, index) => {
                          const actualIndex = index + 6;
                          return (
                            <Card 
                              key={actualIndex} 
                              padding="300"
                              onClick={() => setSelectedEmailIndex(selectedEmailIndex === actualIndex ? null : actualIndex)}
                              style={{ 
                                cursor: 'pointer',
                                border: selectedEmailIndex === actualIndex ? '2px solid #f59e0b' : '1px solid #e5e7eb',
                                backgroundColor: selectedEmailIndex === actualIndex ? '#fffbeb' : 'white'
                              }}
                            >
                              <BlockStack gap="200">
                                <InlineStack gap="200" align="space-between">
                                  <Text variant="headingSm" fontWeight="bold">#{actualIndex + 1}</Text>
                                  <Text variant="bodySm" color="subdued">{t('howItWorks.emailJourney.phases.transit.timing.daily')}</Text>
                                </InlineStack>
                                <Text variant="bodyMd" style={{ lineHeight: '1.4' }}>
                                  {template}
                                </Text>
                              </BlockStack>
                            </Card>
                          );
                        })}
                      </InlineGrid>
                    </BlockStack>
                  </Card>

                  {/* Phase 3: Out for Delivery (Email 21) */}
                  <Card padding="400">
                    <BlockStack gap="400">
                      <InlineStack gap="300" align="start">
                        <div style={{ 
                          backgroundColor: '#fef2f2', 
                          padding: '16px', 
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '60px',
                          minHeight: '60px'
                        }}>
                          <Icon source={HomeMajor} color="critical" />
                        </div>
                        <BlockStack gap="200">
                          <InlineStack gap="200" align="start">
                            <Text variant="headingMd" as="h3">🏠 {t('howItWorks.emailJourney.phases.outForDelivery.title')}</Text>
                            <Badge tone="attention">Email 21</Badge>
                          </InlineStack>
                          <Text color="subdued">
                            {t('howItWorks.emailJourney.phases.outForDelivery.description')}
                          </Text>
                        </BlockStack>
                      </InlineStack>
                      
                      <Card 
                        padding="400"
                        onClick={() => setSelectedEmailIndex(selectedEmailIndex === 20 ? null : 20)}
                        style={{ 
                          cursor: 'pointer',
                          border: selectedEmailIndex === 20 ? '2px solid #ef4444' : '1px solid #e5e7eb',
                          backgroundColor: selectedEmailIndex === 20 ? '#fef2f2' : 'white'
                        }}
                      >
                        <BlockStack gap="200">
                          <InlineStack gap="200" align="space-between">
                            <Text variant="headingMd" fontWeight="bold">#21 - {t('howItWorks.emailJourney.phases.outForDelivery.title')}</Text>
                            <Text variant="bodyMd" color="subdued">{t('howItWorks.emailJourney.phases.outForDelivery.timing.realTime')}</Text>
                          </InlineStack>
                          <Text variant="bodyLg" style={{ lineHeight: '1.5' }}>
                            {emailTemplates[20]}
                          </Text>
                        </BlockStack>
                      </Card>
                    </BlockStack>
                  </Card>

                  {/* Phase 4: Delivered (Email 22) */}
                  <Card padding="400">
                    <BlockStack gap="400">
                      <InlineStack gap="300" align="start">
                        <div style={{ 
                          backgroundColor: '#f0fdf4', 
                          padding: '16px', 
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '60px',
                          minHeight: '60px'
                        }}>
                          <Icon source={ChecklistMajor} color="success" />
                        </div>
                        <BlockStack gap="200">
                          <InlineStack gap="200" align="start">
                            <Text variant="headingMd" as="h3">✅ {t('howItWorks.emailJourney.phases.delivered.title')}</Text>
                            <Badge tone="success">Email 22</Badge>
                          </InlineStack>
                          <Text color="subdued">
                            {t('howItWorks.emailJourney.phases.delivered.description')}
                          </Text>
                        </BlockStack>
                      </InlineStack>
                      
                      <Card 
                        padding="400"
                        onClick={() => setSelectedEmailIndex(selectedEmailIndex === 21 ? null : 21)}
                        style={{ 
                          cursor: 'pointer',
                          border: selectedEmailIndex === 21 ? '2px solid #22c55e' : '1px solid #e5e7eb',
                          backgroundColor: selectedEmailIndex === 21 ? '#f0fdf4' : 'white'
                        }}
                      >
                        <BlockStack gap="200">
                          <InlineStack gap="200" align="space-between">
                            <Text variant="headingMd" fontWeight="bold">#22 - {t('howItWorks.emailJourney.phases.delivered.title')}</Text>
                            <Text variant="bodyMd" color="subdued">{t('howItWorks.emailJourney.phases.delivered.timing.confirmation')}</Text>
                          </InlineStack>
                          <Text variant="bodyLg" style={{ lineHeight: '1.5' }}>
                            {emailTemplates[21]}
                          </Text>
                        </BlockStack>
                      </Card>
                    </BlockStack>
                  </Card>
                </BlockStack>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Tracking Platforms - Modern Design */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <Text variant="headingMd" as="h2">
                {t('howItWorks.trackingPlatforms.title')}
              </Text>
            </Box>
            <Divider />
            
            <Box padding="400">
              <BlockStack gap="400">
                <InlineGrid columns={{ xs: '1', sm: '2' }} gap="400">
                  <CalloutCard
                    title={t('howItWorks.trackingPlatforms.online.title')}
                    illustration="https://cdn.shopify.com/s/files/1/0789/7505/2085/files/tracking-illustration.png"
                    primaryAction={{
                      content: t('howItWorks.trackingPlatforms.online.url'),
                      disabled: true
                    }}
                  >
                    <p>{t('howItWorks.trackingPlatforms.online.description')}</p>
                  </CalloutCard>

                  <CalloutCard
                    title={t('howItWorks.trackingPlatforms.store.title')}
                    illustration="https://cdn.shopify.com/s/files/1/0789/7505/2085/files/store-illustration.png"
                    primaryAction={{
                      content: t('howItWorks.trackingPlatforms.store.url'),
                      disabled: true
                    }}
                  >
                    <p>{t('howItWorks.trackingPlatforms.store.description')}</p>
                  </CalloutCard>
                </InlineGrid>

                <div style={{
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #22c55e',
                  borderRadius: '6px',
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'start',
                  gap: '12px'
                }}>
                  <div style={{
                    backgroundColor: '#22c55e',
                    borderRadius: '4px',
                    padding: '6px',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon source={ChecklistMajor} color="base" />
                  </div>
                  <p style={{ color: '#166534', margin: '0', lineHeight: '1.5', flex: 1 }}>
                    {t('howItWorks.trackingPlatforms.aiChatbot')}
                  </p>
                </div>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Available Languages - Modern Design */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <InlineStack gap="300" align="start">
                <div style={{ 
                  backgroundColor: '#f0f9ff', 
                  padding: '12px', 
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icon source={LanguageMinor} color="primary" />
                </div>
                <BlockStack gap="200">
                  <Text variant="headingMd" as="h2">
                    {t('howItWorks.languages.title')}
                  </Text>
                  <Text color="subdued">
                    {t('howItWorks.languages.description')}
                  </Text>
                </BlockStack>
              </InlineStack>
            </Box>
            <Divider />
            
            <Box padding="400">
              <InlineStack gap="200" wrap>
                {availableLanguages.map((language, index) => (
                  <Badge key={index} tone="info">
                    {language}
                  </Badge>
                ))}
              </InlineStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Benefits Summary - Modern Design */}
        <Layout.Section>
          <Card>
            <Box padding="400" style={{ backgroundColor: '#f0fdf4' }}>
              <InlineStack gap="300" align="start">
                <div style={{ 
                  backgroundColor: '#22c55e', 
                  padding: '12px', 
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icon source={CircleTickMajor} color="base" />
                </div>
                <BlockStack gap="300">
                  <Text variant="headingMd" as="h2" style={{ color: '#166534' }}>
                    {t('howItWorks.benefits.title')}
                  </Text>
                  
                  <List type="bullet">
                    <List.Item style={{ color: '#166534' }}>{t('howItWorks.benefits.proactiveEmails')}</List.Item>
                    <List.Item style={{ color: '#166534' }}>{t('howItWorks.benefits.supportReduction')}</List.Item>
                    <List.Item style={{ color: '#166534' }}>{t('howItWorks.benefits.chargebackReduction')}</List.Item>
                    <List.Item style={{ color: '#166534' }}>{t('howItWorks.benefits.customerSatisfaction')}</List.Item>
                    <List.Item style={{ color: '#166534' }}>{t('howItWorks.benefits.realTimeTracking')}</List.Item>
                  </List>
                </BlockStack>
              </InlineStack>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default HowItWorks;